/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { useStoreDispatch } from "../../../store/hooks";
import { useSelector } from "react-redux";
import { globalTemplateSelector } from "../../../store/api/devices/selectors";
import { basicDialogSelector, isLoadingStatusSelector } from "../../../store/helpers/helperSelectors";
import { setBasicDialogOpenFor } from "../../../store/helpers/helperReducers";
import { DIALOGOPENFOR } from "../../../globals/globals";
import { setSelectedType } from "../../../store/api/devices/reducers";
import ApplyTemplates from "./ApplyTemplates";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import ProfilePermissions from "../../../utils/ProfilePermissions";
import { useLocation } from "react-router-dom";
import { ISelectedTemplate } from "../../../store/api/devices/types";
import { Button } from "../../ui/button";
import { Loader2 } from "lucide-react";

const GlobalTemplate: React.FC = () => {
    const dispatch = useStoreDispatch();
    const location = useLocation();
    const devicePathKey = location?.pathname?.split("/")[2];
    const basicDialogOpenFor = useSelector(basicDialogSelector);
    const globalTemplateSelected: ISelectedTemplate = useSelector(globalTemplateSelector);
    const isLoadingStatus = useSelector(isLoadingStatusSelector);
    const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

    const globalTemplateClickHandler = () => {
        dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.GLOBALTEMPLATE));
        dispatch(setSelectedType("global"));
    }

    return (
        <>
            {basicDialogOpenFor === DIALOGOPENFOR.GLOBALTEMPLATE && <ApplyTemplates modelOpenMode={"Add"} />}
            <div className="grid grid-cols-[auto_1fr_auto] items-center w-full px-4 py-3 rounded-md border-b border-border transition-colors duration-200 hover:bg-muted">

                {/* Left */}
                <div>
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#dbeafe] dark:bg-[#1d2e54]">
                        <span className="flex items-center justify-center h-5 w-5 rounded-full bg-[#c5d2e4] dark:bg-[#344365] text-primary dark:text-[#38BDF8] text-sm font-medium">
                            1
                        </span>
                        <span className="text-sm font-medium text-primary dark:text-[#38BDF8]">
                            Global template
                        </span>
                    </div>
                </div>

                {/* Center */}
                <div className="flex justify-center text-sm">
                    {globalTemplateSelected?.deviceKey === devicePathKey && (
                        <>
                            <span className="text-muted-foreground whitespace-nowrap">Template Name:&nbsp;</span>
                            <span className="text-foreground">{globalTemplateSelected?.templateName || ""}</span>
                        </>
                    )}
                </div>

                {/* Right */}
                <div className="flex justify-end">
                    {permissions?.hasOpCodeGroup("manage_sn") && (
                        <Button
                            onClick={globalTemplateClickHandler}
                            variant="outline"
                            size="sm"
                            disabled={isLoadingStatus["apply"]}
                        >
                            {isLoadingStatus["apply"] && (
                                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            )}
                            Apply
                        </Button>
                    )}
                </div>
            </div>
        </>
    )

}
export default GlobalTemplate;